import { axios, APIBASE_REVIVERAPPLICATION, APIBASE_REVIVERUSERS } from "./ApiConfig";
import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import {
  Company,
  DecideDeviationsDto,
  DeviationDecisionDto,
  DeviationDecisionPreviewDto,
  InspectionDto,
  InspectionFraction,
  InspectionImage,
  InspectionListFilters,
  InspectionListItem,
  InspectionPreviewDto,
  OrderInspection,
  ReceivingInspectionSettings,
  SubmitInspectionDto,
  SubmitInspectionProductDto,
  SubmitInspectionServiceDto,
  SubmitInspectionStreamDto,
} from "@/types";
import { compressImage, fileToBase64Raw } from "@/utils/FileUtils";

const getReceivingInspectionSettings = (companyId: number) =>
  axios.get<ReceivingInspectionSettings>(
    `${APIBASE_REVIVERUSERS}api/company/settings/receivingInspection/${companyId}`
  );

export const useGetReceivingInspectionSettings = (
  companyId: number,
  options?: Partial<UseQueryOptions<ReceivingInspectionSettings>>
) =>
  useQuery<ReceivingInspectionSettings>({
    queryKey: ["receivingInspectionSettings"],
    queryFn: async () => (await getReceivingInspectionSettings(companyId)).data,
    refetchOnWindowFocus: false,
    enabled: !!companyId,
    ...options,
  });

/** Replaces all the settings; a field left out is cleared. Admins only. */
const putReceivingInspectionSettings = (companyId: number, settings: ReceivingInspectionSettings) =>
  axios.put<string>(`${APIBASE_REVIVERUSERS}api/company/settings/receivingInspection/${companyId}`, settings);

export const useUpdateReceivingInspectionSettings = (
  companyId: number,
  options?: UseMutationOptions<ReceivingInspectionSettings, Error, ReceivingInspectionSettings>
) => {
  const queryClient = useQueryClient();

  return useMutation<ReceivingInspectionSettings, Error, ReceivingInspectionSettings>({
    ...options,
    // The backend answers "Updated", not the settings, so what was sent is what's now stored.
    mutationFn: async (settings) => {
      await putReceivingInspectionSettings(companyId, settings);
      return settings;
    },
    onSuccess: (...args) => {
      const [saved] = args;
      queryClient.setQueryData(["receivingInspectionSettings"], saved);
      // The company carries the same fields, and other settings pages POST the whole cached company back:
      // keep it current so they can't overwrite this with stale values.
      queryClient.setQueryData<Company>(["getCompanyById", companyId], (company) =>
        company ? { ...company, ...saved } : company
      );
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", companyId] });
      options?.onSuccess?.(...args);
    },
  });
};

const getInspections = (filters: InspectionListFilters) =>
  axios.get<InspectionListItem[]>(`${APIBASE_REVIVERAPPLICATION}api/inspections`, { params: filters });

/** The company's buy orders awaiting inspection, oldest planned date first. */
export const useGetInspections = (
  filters: InspectionListFilters,
  options?: Partial<UseQueryOptions<InspectionListItem[]>>
) =>
  useQuery<InspectionListItem[]>({
    queryKey: ["inspections", filters],
    queryFn: async () => (await getInspections(filters)).data ?? [],
    refetchOnWindowFocus: false,
    ...options,
  });

const getOrderInspection = (orderId: number) =>
  axios.get<OrderInspection>(`${APIBASE_REVIVERAPPLICATION}api/inspections/orders/${orderId}`);

/** The weight prefill and any submitted inspections, for the buyer or the seller. */
export const useGetOrderInspection = (orderId: number | null, options?: Partial<UseQueryOptions<OrderInspection>>) =>
  useQuery<OrderInspection>({
    queryKey: ["orderInspection", orderId],
    queryFn: async () => (await getOrderInspection(orderId as number)).data,
    refetchOnWindowFocus: false,
    enabled: !!orderId,
    ...options,
  });

const getInspectionFractions = (orderId: number) =>
  axios.get<InspectionFraction[]>(`${APIBASE_REVIVERAPPLICATION}api/inspections/orders/${orderId}/fractions`);

/** The fractions on the buyer's other active contracts with the same seller, which extra streams can be put on. */
export const useGetInspectionFractions = (
  orderId: number | null,
  options?: Partial<UseQueryOptions<InspectionFraction[]>>
) =>
  useQuery<InspectionFraction[]>({
    queryKey: ["inspectionFractions", orderId],
    queryFn: async () => (await getInspectionFractions(orderId as number)).data ?? [],
    refetchOnWindowFocus: false,
    enabled: !!orderId,
    ...options,
  });

/** A part of the submit body with its photos as picked, before they're encoded. */
type WithPhotoFiles<T> = Omit<T, "photos"> & { photos: File[] };

export type SubmitInspectionProduct = WithPhotoFiles<SubmitInspectionProductDto>;
export type SubmitInspectionService = WithPhotoFiles<SubmitInspectionServiceDto>;
export type SubmitInspectionStream = WithPhotoFiles<SubmitInspectionStreamDto>;

/** The submit body before the photos are encoded. */
export interface SubmitInspection extends Omit<
  SubmitInspectionDto,
  "photos" | "additionalStreams" | "products" | "services"
> {
  photos: File[];
  additionalStreams: SubmitInspectionStream[];
  products: SubmitInspectionProduct[];
  services: SubmitInspectionService[];
}

const toInspectionImage = async (file: File): Promise<InspectionImage> => {
  const compressed = await compressImage(file);
  return {
    image: await fileToBase64Raw(compressed),
    // A JPEG, or the original when it couldn't be decoded; the photo picker only takes JPEG and PNG.
    fileType: compressed.type === "image/png" ? "PNG" : "JPG",
  };
};

/** One photo at a time: decoding up to 220 full-size photos at once can run a phone out of memory. */
const toInspectionImages = async (files: File[]) => {
  const images: InspectionImage[] = [];
  for (const file of files) images.push(await toInspectionImage(file));
  return images;
};

/** Encodes the photos of each stream or deviation, one after the other. */
const withEncodedPhotos = async <T extends { photos: File[] }>(items: T[]) => {
  const encoded: (Omit<T, "photos"> & { photos: InspectionImage[] })[] = [];
  for (const item of items) encoded.push({ ...item, photos: await toInspectionImages(item.photos) });
  return encoded;
};

const postInspection = async (
  orderId: number,
  { photos, additionalStreams, products, services, ...rest }: SubmitInspection
) => {
  const body: SubmitInspectionDto = {
    ...rest,
    photos: await toInspectionImages(photos),
    additionalStreams: await withEncodedPhotos(additionalStreams),
    products: await withEncodedPhotos(products),
    services: await withEncodedPhotos(services),
  };
  return axios.post<InspectionDto>(`${APIBASE_REVIVERAPPLICATION}api/inspections/orders/${orderId}`, body);
};

export interface SubmitInspectionVariables {
  orderId: number;
  inspection: SubmitInspection;
}

export const useSubmitInspection = (options?: UseMutationOptions<InspectionDto, Error, SubmitInspectionVariables>) => {
  const queryClient = useQueryClient();

  const invalidate = (orderId: number) => {
    // The order is finalized and extra streams create new orders, so every list that shows them is stale.
    queryClient.invalidateQueries({ queryKey: ["inspections"] });
    queryClient.invalidateQueries({ queryKey: ["getOrdersForCompany"] });
    queryClient.invalidateQueries({ queryKey: ["getOrderById", orderId] });
    queryClient.invalidateQueries({ queryKey: ["orderInspection", orderId] });
    queryClient.invalidateQueries({ queryKey: ["getContractById"] });
    queryClient.invalidateQueries({ queryKey: ["getOpenTasks"] });
  };

  return useMutation<InspectionDto, Error, SubmitInspectionVariables>({
    ...options,
    mutationFn: async ({ orderId, inspection }) => (await postInspection(orderId, inspection)).data,
    onSuccess: (...args) => {
      invalidate(args[1].orderId);
      options?.onSuccess?.(...args);
    },
    onError: (...args) => {
      // A 409 means the order was inspected or moved on elsewhere, so the lists are stale too.
      invalidate(args[1].orderId);
      options?.onError?.(...args);
    },
  });
};

/**
 * The preview only prices, so it's sent the submit body without photos or free text. The body is also the query
 * key, so typing a registration number, PO number or comment doesn't price the deviations again.
 */
const toPreviewBody = ({
  photos,
  additionalStreams,
  products,
  services,
  ...rest
}: SubmitInspection): SubmitInspectionDto => {
  const withoutPhotos = <T extends { photos: File[] }>(items: T[]) => items.map((item) => ({ ...item, photos: [] }));
  return {
    ...rest,
    transportRegistrationNumber: null,
    poNumber: null,
    comment: null,
    photos: [],
    additionalStreams: withoutPhotos(additionalStreams),
    products: withoutPhotos(products),
    services: withoutPhotos(services),
  };
};

const postInspectionPreview = (orderId: number, body: SubmitInspectionDto) =>
  axios.post<InspectionPreviewDto>(`${APIBASE_REVIVERAPPLICATION}api/inspections/orders/${orderId}/preview`, body);

/**
 * What submitting `inspection` would cost: each part's weight split and deviation costs, priced by the backend
 * as the submit would be. Nothing is stored. Keeps the order's last result while a changed form is recalculated,
 * but never shows another order's.
 */
export const useGetInspectionPreview = (
  orderId: number,
  inspection: SubmitInspection | null,
  options?: Partial<UseQueryOptions<InspectionPreviewDto>>
) => {
  const body = inspection ? toPreviewBody(inspection) : null;
  return useQuery<InspectionPreviewDto>({
    queryKey: ["inspectionPreview", orderId, body],
    queryFn: async () => (await postInspectionPreview(orderId, body as SubmitInspectionDto)).data,
    enabled: !!body,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === orderId ? previous : undefined),
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
};

const postDeviationDecisions = (orderId: number, body: DecideDeviationsDto) =>
  axios.post<OrderInspection>(
    `${APIBASE_REVIVERAPPLICATION}api/inspections/orders/${orderId}/deviation-decisions`,
    body
  );

export interface DecideDeviationsVariables {
  /** The buyer's order, the one the inspection was registered on. */
  orderId: number;
  decisions: DecideDeviationsDto["decisions"];
}

const postDeviationDecisionPreview = (orderId: number, body: DecideDeviationsDto) =>
  axios.post<DeviationDecisionPreviewDto>(
    `${APIBASE_REVIVERAPPLICATION}api/inspections/orders/${orderId}/deviation-decisions/preview`,
    body
  );

/**
 * What saving `decisions` would cost: every deviation repriced as the decide would, and the weight split. Nothing is
 * stored. Deviation managers and admins only. Rejects don't need their comment yet. Keeps the order's last result
 * while changed decisions are recalculated, but never shows another order's.
 */
export const useGetDeviationDecisionPreview = (
  orderId: number,
  decisions: DeviationDecisionDto[] | null,
  options?: Partial<UseQueryOptions<DeviationDecisionPreviewDto>>
) =>
  useQuery<DeviationDecisionPreviewDto>({
    queryKey: ["deviationDecisionPreview", orderId, decisions],
    queryFn: async () =>
      (await postDeviationDecisionPreview(orderId, { decisions: decisions as DeviationDecisionDto[] })).data,
    enabled: !!decisions,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === orderId ? previous : undefined),
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });

/** Confirms or rejects pending deviations. Deviation managers and admins only. */
export const useDecideDeviations = (
  options?: UseMutationOptions<OrderInspection, Error, DecideDeviationsVariables>
) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    // Deciding the last deviation finalizes the order and its streams and completes the task.
    queryClient.invalidateQueries({ queryKey: ["orderInspection"] });
    queryClient.invalidateQueries({ queryKey: ["getOrdersForCompany"] });
    queryClient.invalidateQueries({ queryKey: ["getOrderById"] });
    queryClient.invalidateQueries({ queryKey: ["getContractById"] });
    queryClient.invalidateQueries({ queryKey: ["getOpenTasks"] });
    queryClient.invalidateQueries({ queryKey: ["getOverdueTasks"] });
  };

  return useMutation<OrderInspection, Error, DecideDeviationsVariables>({
    ...options,
    mutationFn: async ({ orderId, decisions }) => (await postDeviationDecisions(orderId, { decisions })).data,
    onSuccess: (...args) => {
      const [inspection, { orderId }] = args;
      queryClient.setQueryData(["orderInspection", orderId], inspection);
      invalidate();
      options?.onSuccess?.(...args);
    },
    onError: (...args) => {
      // 182 and 183 can mean someone else decided meanwhile, so what's shown is stale.
      invalidate();
      options?.onError?.(...args);
    },
  });
};
